import classNames from "classnames";
import moment from "moment";
import React, {FC, memo, useMemo, useState} from "react";
import {Button, message, Modal, notification, Space, Table, Tag, Tooltip} from "antd";
import {LinkOutlined} from "@ant-design/icons";
import {useMutation} from "@apollo/client";
import {
    GetBookingByIdDocument,
    GetBookingByIdQuery,
    GetBookingByIdWithCourseLessonBookingsDocument,
    GetBookingByIdWithCourseLessonBookingsQuery,
    GetPaidScheduledPaymentsDocument,
    GetUnpaidScheduledPaymentsDocument,
    PaymentProvider,
    ScheduledPaymentType,
    useGetAppSettingsQuery,
    useGetCompanyInfoQuery,
    useUpdateBookingPaymentMethodMutation,
} from "../../generated/graphql";
import {classifyMollieDispatchError} from "../../helpers/mollieDispatchError";
import i18n from "../../services/i18n";
import PanelCard from "../PanelCard/PanelCard";
import PaymentMethodBadge from "../PaymentMethodBadge/PaymentMethodBadge";
import {RenderAmount, renderPaymentType} from "../TableColumnRenderers/TableColumnRenderers";
import css from "./PanelCardPaymentData.less";
import PaymentMethodTable from "../PaymentMethodTable/PaymentMethodTable";
import {KnownError, handleError} from "../../errors/handleErrors";
import {detectTransferPaidFromNoticeStaff} from "../../containers/bookingHistory/transferContext";
import {Alert} from "antd";
// Phase 13 (DISP-02 / DISP-04) — Bundle-Dispatch-Mutation.
import {SEND_MOLLIE_PAYMENT_LINK_FOR_BOOKING_FROM_ADMIN} from "../../containers/payments/mollie.graphql";
// Phase 14 / Plan 14-05 (D-13) — Standalone-Refund-Button fuer Booking-Detail.
import MollieRefundButton from "../MollieRefundButton/MollieRefundButton";
import type {RefundPanelRow} from "../RefundPanel/RefundPanel";
import {getDisplayedPaymentMethodType} from "../../helpers/getDisplayedPaymentMethodType";

type PossibleBookingType = GetBookingByIdQuery["bookingById"] | GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"];

type PanelCardPaymentDataProps = {
    // booking: GetBookingByIdQuery["bookingById"];
    booking: PossibleBookingType;
    refetchScheduledPayments: () => void;
    isCanceled: boolean;
    className?: string;
    /**
     * Phase 23 / Plan 23-04 / BDSEC-06 — Mollie-Mode-Variante.
     *
     * Wenn true:
     *  - Hellblauer Card-Hintergrund (`.mollie-mode` CSS-Klasse, Tokens aus
     *    `mollie-design-tokens.md`: `--mollie-bg` + `--color-primary-border`).
     *  - Mollie-Logo-Badge im Card-Title-Slot ("mollie"-Pill).
     *  - Edit-Action-Button + Bundle-Dispatch-Button werden NICHT gerendert
     *    (Hosted-Checkout: Kunde waehlt im Mollie-UI; Admin kann hier nichts
     *    editieren).
     *
     * Default false → byte-identisches Verhalten zur Pre-Phase-23-Variante.
     * Wird vom Strategy-Switch in `SettlementDetails.tsx` (Plan 23-04 Task 4)
     * gespeist: `mollieMode={booking.course.allowMolliePayment === true}`.
     */
    mollieMode?: boolean;
};

/**
 * WP0b — Prädikat für bündelbare offene Mollie-Posten (Sammelzahlung).
 * Offen + Mollie + KEIN Discount (Gutschriften können nicht eingezogen werden,
 * analog Server-Guard createBundleDispatchPaymentLink). Custom/CancelationFee
 * sind eingeschlossen. Exportiert, damit die Einschluss-Regel direkt testbar ist.
 */
export const isOpenMollieBundleSP = (sp: {
    paymentProvider: PaymentProvider;
    paymentDone?: Date | string | null;
    type: ScheduledPaymentType;
}): boolean =>
    sp.paymentProvider === PaymentProvider.Mollie &&
    !sp.paymentDone &&
    sp.type !== ScheduledPaymentType.Discount;

const PriceOverview: FC<{booking: PossibleBookingType}> = ({booking}) => {
    const creditedBalance = booking.creditedBalance ?? 0;
    const memberDiscount = booking.memberDiscount ?? 0;
    const discount = booking.discount ?? 0;
    const grossPrice = booking.grossPrice ?? 0;
    const additionalFees = (booking.scheduledPayments ?? []).filter(
        (sp) => sp.type !== ScheduledPaymentType.Standard && sp.type !== ScheduledPaymentType.Discount
    );
    const additionalFeesSum = additionalFees.reduce((acc, sp) => acc + Number(sp.paymentAmount), 0);
    // Umbuchungs-Engine (ADR 0006): der Übertrag aus der bisherigen Buchung steht strukturiert
    // am Vorgangs-Claim. Er ist keine Preisminderung, sondern eine Zahlung — deshalb unter dem
    // Buchungspreis als eigener Block „bereits bezahlt / noch offen" (Abnahme 22.09.2026).
    const transferIn = "transferIn" in booking ? booking.transferIn : undefined;
    // Rabatt-Zeilen sind eigene Posten mit negativem Betrag, die nie „bezahlt" werden — wie bei
    // `additionalFees` ausschließen, sonst fällt „Noch offen" um den Rabatt zu niedrig aus.
    const openTotal = (booking.scheduledPayments ?? [])
        .filter((sp) => sp.paymentDone == null && sp.type !== ScheduledPaymentType.Discount)
        .reduce((acc, sp) => acc + Number(sp.openAmount ?? sp.paymentAmount), 0);
    const hasAnyDeduction = creditedBalance > 0 || memberDiscount > 0 || discount > 0 || additionalFees.length > 0 || Boolean(transferIn);
    const originalPrice = grossPrice + creditedBalance + memberDiscount + discount - additionalFeesSum;

    type ContactCardCSSProperties = React.CSSProperties & {"--column-count": number};
    const containerStyle: ContactCardCSSProperties = {"--column-count": 1};

    const row: React.CSSProperties = {display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, lineHeight: "20px"};
    const deduction: React.CSSProperties = {color: "#979ea5", fontSize: 12};

    // Nachvollziehbarkeit beim Umbuchen: Wenn der Buchungspreis 0 ist UND die
    // noticeStaff-Notiz einen Übertrag aus einer Umbuchung dokumentiert, zeigen
    // wir einen rein informativen grünen Hinweis. Rein kosmetisch — keine
    // Settlement-/Dunning-/Status-Logik. Quelle: noticeStaff-Marker
    // ("Übertrag von ... durch Umbuchung"), robust im Komponenten-Kontext.
    const transferPaid = detectTransferPaidFromNoticeStaff(booking.noticeStaff);
    const showLegacyTransferPaidHint = !transferIn && grossPrice === 0 && transferPaid.isTransferPaid;
    const legacyTransferPaidMessage = transferPaid.amount
        ? `Bereits bezahlt durch Übertrag aus einer anderen Buchung (${transferPaid.amount} ${transferPaid.currency ?? ""}).`.trim()
        : "Bereits bezahlt durch Übertrag aus einer anderen Buchung.";

    // Legacy-Umbuchung (Marker in noticeStaff): in BEIDEN Render-Zweigen verfügbar — eine
    // 0-€-Transfer-Buchung kann theoretisch auch hasAnyDeduction=true haben (z.B. Rabatt-Posten).
    const transferPaidHint = showLegacyTransferPaidHint ? (
        <Alert
            type="success"
            showIcon
            style={{marginTop: 8}}
            message={legacyTransferPaidMessage}
            data-testid="transfer-paid-hint"
        />
    ) : null;

    if (!hasAnyDeduction) {
        return (
            <div className={classNames(css.container)} style={containerStyle}>
                <div className={css.label}>{i18n.containers.invoice.InvoiceDetails.bookingPrice()}</div>
                <div className={css.entry}>
                    <RenderAmount amount={grossPrice} style={{fontWeight: 550}} />
                </div>
                {transferPaidHint}
            </div>
        );
    }

    return (
        <div style={{marginTop: 5, fontSize: 13}}>
            <div className={css.label} style={{marginBottom: 2, fontSize: 14, paddingTop: 10}}>
                {"Preisübersicht"}
            </div>
            <div style={row}>
                <div>{"Buchungspreis (Original)"}</div>
                <RenderAmount amount={originalPrice} style={{fontSize: 13}} />
            </div>
            {memberDiscount > 0 && (
                <div style={{...row, ...deduction}}>
                    <div>{"Mitgliederrabatt"}</div>
                    <div style={deduction}>
                        {"- "}
                        <RenderAmount amount={memberDiscount} style={{display: "inline", fontSize: 12}} />
                    </div>
                </div>
            )}
            {discount > 0 && (
                <div style={{...row, ...deduction}}>
                    <div>{booking.discountName || "Rabatt"}</div>
                    <div style={deduction}>
                        {"- "}
                        <RenderAmount amount={discount} style={{display: "inline", fontSize: 12}} />
                    </div>
                </div>
            )}
            {creditedBalance > 0 && (
                <div style={{...row, ...deduction}}>
                    <div>{"Verrechnetes Guthaben"}</div>
                    <div style={deduction}>
                        {"- "}
                        <RenderAmount amount={creditedBalance} style={{display: "inline", fontSize: 12}} />
                    </div>
                </div>
            )}
            {additionalFees.map((fee) => (
                <div key={fee.id} style={{...row}}>
                    <div>{fee.title ?? fee.type}</div>
                    <div>
                        {"+ "}
                        <RenderAmount amount={fee.paymentAmount} style={{display: "inline", fontSize: 13}} />
                    </div>
                </div>
            ))}
            <div style={{borderTop: "1px solid #e8e8e8", margin: "3px 0"}} />
            <div style={{...row, fontWeight: 600}}>
                <div>{"Buchungspreis"}</div>
                <RenderAmount amount={grossPrice} style={{fontWeight: 600, fontSize: 13}} />
            </div>
            {transferIn && (
                <div data-testid="transfer-paid-hint">
                    <div style={{...row, ...deduction, marginTop: 6}}>
                        <div>{`Per Übertrag aus ${transferIn.sourceBookingNumber} bezahlt`}</div>
                        <div style={deduction}>
                            {"- "}
                            <RenderAmount amount={transferIn.appliedAmount} style={{display: "inline", fontSize: 12}} />
                        </div>
                    </div>
                    <div style={{borderTop: "1px solid #e8e8e8", margin: "3px 0"}} />
                    <div style={{...row, fontWeight: 600}}>
                        <div>{"Noch offen"}</div>
                        <RenderAmount amount={openTotal} style={{fontWeight: 600, fontSize: 13}} />
                    </div>
                </div>
            )}
            {transferPaidHint}
        </div>
    );
};

type EditPaymentModalProps = {
    booking: PossibleBookingType;
    refetchScheduledPayments: () => void;
    onClose: () => void;
};

/**
 * Bewusst eine eigene Komponente statt inline in PanelCardPaymentData definiert: inline hätte sie
 * bei jedem Render eine neue Identität, React würde sie neu montieren und die Auswahl fiele auf
 * die gespeicherte Methode zurück — z. B. nachdem das Anlegen einer Bezahlmethode `booking` per
 * Refetch ersetzt hat. Der Aufrufer montiert sie nur, solange der Dialog offen ist; jedes Öffnen
 * startet so mit der gespeicherten Methode.
 */
const EditPaymentModal: FC<EditPaymentModalProps> = ({booking, refetchScheduledPayments, onClose}) => {
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(booking.paymentMethod);
    // Solange „Zahlmethode ändern“ offen ist, keinen Footer zeigen: „Bezahlmethode
    // speichern“ würde sonst eine begonnene Neuanlage still verwerfen.
    const [changeSectionOpen, setChangeSectionOpen] = useState(false);

    const [updatePaymentMethod] = useUpdateBookingPaymentMethodMutation({
        variables: {
            bookingId: booking.id,
            paymentMethodId: selectedPaymentMethod?.id ?? "",
        },
        refetchQueries: [
            "GetBookingById",
            {query: GetBookingByIdDocument, variables: {id: booking.id}},
            "GetPaidScheduledPayments",
            {
                query: GetPaidScheduledPaymentsDocument,
                variables: {bookingId: booking.id},
            },
            "GetUnpaidScheduledPayments",
            {
                query: GetUnpaidScheduledPaymentsDocument,
                variables: {bookingId: booking.id},
            },
            "GetBookingByIdWithCourseLessonBookings",
            {
                query: GetBookingByIdWithCourseLessonBookingsDocument,
                variables: {id: booking.id},
            },
        ],
    });

    const paymentMethodsOfBooker = booking?.attendee?.booker?.paymentMethods;

    // Phase 12 / Plan 12-02 / LIFE-04 (D-12): Pre-Confirm-Modal-Handler
    // lebt hier (Booking-Level, Open Question #3 RESOLVED), NICHT in
    // PaymentMethodTable (Attendee-Default-Level, D-04).
    const handleSave = async () => {
        const knownErrors: Array<KnownError> = [
            {
                key: "Booking is billed",
                title: "Buchung bereits abgerechnet",
                msg: "Sobald eine Buchung abgerechnet ist, kann deren Bezahlmethode nicht mehr geändert werden.",
            },
            // Phase 12 / LIFE-01 Defense-in-Depth: wenn das Backend-Guard
            // (assertMolliePaymentNotLocked) doch zuschlaegt, zeigen wir einen
            // user-freundlichen Modal statt des generischen GraphQL-ForbiddenError.
            // Der Key matcht den Wortlaut aus scheduledPaymentMollieGuard.ts.
            {
                key: "Bezahlmethode dieser Rate kann nicht geändert werden",
                title: i18n.views.PanelCardPaymentData.mollieLifecycleForbiddenErrorTitle(),
                msg: i18n.views.PanelCardPaymentData.mollieLifecycleForbiddenErrorMsg(),
            },
            // B-82808: leere Lastschrift-Methode wurde gewählt. Die Tabelle deaktiviert
            // solche Zeilen bereits; das hier fängt Altfälle/Race-Conditions ab. Key =
            // Wortlaut aus server/booking/paymentMethodIncompleteError.ts (bleibt auch als
            // Substring erhalten, wenn ein Resolver ihn in einen BadRequestError verpackt).
            {
                key: "Lastschrift-Daten unvollständig",
                title: i18n.views.PanelCardPaymentData.paymentMethodIncompleteErrorTitle(),
                msg: i18n.views.PanelCardPaymentData.paymentMethodIncompleteErrorMsg(),
            },
        ];

        const proceed = async () => {
            try {
                await updatePaymentMethod().then(() => {
                    refetchScheduledPayments();
                    onClose();
                });
            } catch (error) {
                handleError("updateBookingPaymentMethod", error, "modal", knownErrors);
            }
        };

        // D-12 Pre-Confirm: client-seitige SP-Kategorisierung (Creative
        // Option b aus CONTEXT.md — kein Roundtrip).
        // Pitfall-1-konforme Null/Undefined-Checks.
        const sps = booking.scheduledPayments ?? [];
        const lockedMollieSPs = sps.filter(
            (sp) =>
                sp.paymentDone !== null &&
                sp.paymentDone !== undefined &&
                sp.paymentProvider === PaymentProvider.Mollie,
        );
        const updatableSPs = sps.filter(
            (sp) => sp.paymentDone === null || sp.paymentDone === undefined,
        );

        // Pre-Confirm NUR bei mixed State (einige gesperrt + einige updatebar).
        // Pure-Paid-State oder Pure-Open-State -> direkter Backend-Call.
        if (lockedMollieSPs.length > 0 && updatableSPs.length > 0) {
            const formatDueDate = (d?: Date | string | null): string => {
                if (!d) return "";
                const date = typeof d === "string" ? new Date(d) : d;
                return date instanceof Date && !Number.isNaN(date.getTime())
                    ? date.toLocaleDateString("de-DE")
                    : String(d);
            };
            const formatAmount = (amount?: number | null): string => {
                if (amount === null || amount === undefined) return "";
                return new Intl.NumberFormat("de-DE", {style: "currency", currency: "EUR"}).format(amount);
            };

            Modal.confirm({
                title: i18n.views.PanelCardPaymentData.mollieLifecyclePreConfirmTitle(),
                width: 600,
                content: (
                    <div>
                        <p>
                            {i18n.views.PanelCardPaymentData.mollieLifecyclePreConfirmUpdatableHeading({
                                count: String(updatableSPs.length),
                            })}
                        </p>
                        <ul>
                            {updatableSPs.map((sp) => (
                                <li key={sp.id}>
                                    {i18n.views.PanelCardPaymentData.mollieLifecyclePreConfirmSPLine({
                                        dueDate: formatDueDate(sp.paymentDue),
                                        amount: formatAmount(sp.paymentAmount),
                                    })}
                                </li>
                            ))}
                        </ul>
                        <p>
                            {i18n.views.PanelCardPaymentData.mollieLifecyclePreConfirmLockedHeading({
                                count: String(lockedMollieSPs.length),
                            })}
                        </p>
                        <ul>
                            {lockedMollieSPs.map((sp) => (
                                <li key={sp.id}>
                                    {i18n.views.PanelCardPaymentData.mollieLifecyclePreConfirmSPLine({
                                        dueDate: formatDueDate(sp.paymentDue),
                                        amount: formatAmount(sp.paymentAmount),
                                    })}
                                </li>
                            ))}
                        </ul>
                    </div>
                ),
                okText: i18n.views.PanelCardPaymentData.mollieLifecyclePreConfirmOk(),
                cancelText: i18n.views.PanelCardPaymentData.mollieLifecyclePreConfirmCancel(),
                onOk: proceed,
            });
        } else {
            await proceed();
        }
    };

    return (
        <Modal
            title="Bezahlmethode bearbeiten"
            open
            onCancel={onClose}
            width={800}
            onOk={handleSave}
            okText="Bezahlmethode speichern"
            footer={changeSectionOpen ? null : undefined}
        >
            <PaymentMethodTable
                paymentMethodId={selectedPaymentMethod?.id}
                paymentMethods={paymentMethodsOfBooker ?? []}
                setSelectedPayment={setSelectedPaymentMethod}
                bookerId={booking.attendee?.booker?.id}
                bookingId={booking.id}
                storedPaymentMethodId={booking.paymentMethod?.id}
                courseId={booking.courseId ?? undefined}
                activeOnly
                onChangeSectionToggle={setChangeSectionOpen}
                // Cast weil booking.scheduledPayments ein Union zweier
                // Query-Shapes ist; PaymentMethodTable konsumiert nur
                // paymentDone + paymentProvider (Pitfall-1-konforme Checks).
                scheduledPayments={booking.scheduledPayments as any}
            />
        </Modal>
    );
};

const PanelCardPaymentData: FC<PanelCardPaymentDataProps> = ({booking, refetchScheduledPayments, isCanceled, className, mollieMode = false}) => {
    const paymentMethodType = getDisplayedPaymentMethodType(booking.paymentMethod ?? {});

    // Pruefe ob Mollie als bankTransferProvider konfiguriert ist
    const {data: appSettingsData} = useGetAppSettingsQuery();
    const isMollieBankTransfer = appSettingsData?.settings?.bankTransferProvider === PaymentProvider.Mollie;

    // Mollie-Awareness: Suche den neuesten ScheduledPayment, der tatsächlich via
    // Mollie abgewickelt wurde — der Buchungs-Default `paymentMethod` kann
    // veraltet sein (z.B. "Bar"), obwohl die EUA-Zahlung per Kreditkarte/PayPal
    // lief. Kein Match → Badge fällt auf klassische Darstellung zurück.
    const scheduledPayments = booking.scheduledPayments ?? [];
    const mollieSp = [...scheduledPayments]
        .reverse()
        .find((sp) => sp.paymentProvider === PaymentProvider.Mollie && Boolean(sp.mollieMethod));
    const isMolliePaid = Boolean(mollieSp);

    const [editPaymentModalOpen, setEditPaymentModalOpen] = useState(false);

    // Phase 13 (DISP-02, DISP-04, D-09, D-15, D-16) — Bundle-Dispatch.
    const {data: companyInfoData} = useGetCompanyInfoQuery();
    const featureMollie = Boolean(
        companyInfoData?.companyInfo?.subscription?.featureMollie,
    );

    // Offene Mollie-SPs der Buchung — gating fuer Bundle-Button-Verfuegbarkeit (D-09).
    // WP0b-Gotcha: Bündelbar sind ALLE offenen Mollie-Posten außer Discount
    // (siehe isOpenMollieBundleSP). Custom/CancelationFee werden mitgebündelt.
    const openMollieSPs = useMemo(
        () => scheduledPayments.filter(isOpenMollieBundleSP),
        [scheduledPayments],
    );

    // Phase 14 / Plan 14-05 (D-13, D-15) — Bezahlte Mollie-SPs mit refundedAmount < paymentAmount.
    // Gating fuer Standalone-Refund-Button im Header. RefundPanelRow-Mapping siehe unten
    // (Button-Einhaengen).
    const refundableMollieSPs = useMemo<RefundPanelRow[]>(
        () =>
            scheduledPayments
                .filter(
                    (sp) =>
                        sp.paymentProvider === PaymentProvider.Mollie
                        && sp.paymentDone
                        && Number(sp.paymentAmount) > Number((sp as {refundedAmount?: number}).refundedAmount || 0),
                )
                .map((sp) => ({
                    scheduledPaymentId: sp.id,
                    paymentAmount: Number(sp.paymentAmount),
                    refundedAmount: Number((sp as {refundedAmount?: number}).refundedAmount || 0),
                    paymentDue: sp.paymentDue,
                    paymentDone: sp.paymentDone ?? null,
                    title: sp.title ?? null,
                    paymentProvider: sp.paymentProvider,
                    mollieMethod: sp.mollieMethod ?? null,
                })),
        [scheduledPayments],
    );

    const canDispatchBundle = openMollieSPs.length >= 2;
    const [bundleRateLimitUntil, setBundleRateLimitUntil] = useState<number | null>(null);
    const [sendBundleLink, {loading: bundleLoading}] = useMutation(
        SEND_MOLLIE_PAYMENT_LINK_FOR_BOOKING_FROM_ADMIN,
    );

    const handleBundleDispatch = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const recipientEmail: string = (booking.attendee?.booker as any)?.email ?? "";
        const totalAmount = openMollieSPs.reduce(
            (sum, sp) => sum + Number(sp.paymentAmount || 0),
            0,
        );
        // Annahme: Aktuelle Konfiguration arbeitet primaer in EUR.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const currency = (booking as any)?.currency ?? "EUR";

        Modal.confirm({
            title: i18n.views.PanelCardPaymentData.bundleDispatchModalTitle(),
            okText: i18n.views.PanelCardPaymentData.bundleDispatchModalOkText(),
            cancelText: i18n.views.PanelCardPaymentData.bundleDispatchModalCancelText(),
            width: 720,
            content: (
                <div>
                    <p>{i18n.views.PanelCardPaymentData.bundleDispatchModalIntro()}</p>
                    <Table
                        dataSource={openMollieSPs.map((sp) => ({
                            key: sp.id,
                            paymentDue: sp.paymentDue
                                ? moment(sp.paymentDue).format("DD.MM.YY")
                                : "",
                            description: sp.title ?? "",
                            paymentAmount:
                                Number(sp.paymentAmount).toFixed(2).replace(".", ",") +
                                " " +
                                currency,
                        }))}
                        columns={[
                            {
                                title: i18n.views.PanelCardPaymentData.bundleDispatchModalColumnDueDate(),
                                dataIndex: "paymentDue",
                                key: "paymentDue",
                            },
                            {
                                title: i18n.views.PanelCardPaymentData.bundleDispatchModalColumnTitle(),
                                dataIndex: "description",
                                key: "description",
                            },
                            {
                                title: i18n.views.PanelCardPaymentData.bundleDispatchModalColumnAmount(),
                                dataIndex: "paymentAmount",
                                key: "paymentAmount",
                            },
                        ]}
                        pagination={false}
                        size="small"
                    />
                    <p style={{marginTop: 16, fontWeight: 600}}>
                        {i18n.views.PanelCardPaymentData.bundleDispatchModalTotal({
                            amount: totalAmount.toFixed(2).replace(".", ","),
                            currency,
                        })}
                    </p>
                    <p>
                        {i18n.views.PanelCardPaymentData.bundleDispatchModalRecipient({
                            email: recipientEmail,
                        })}
                    </p>
                    <p>
                        {i18n.views.PanelCardPaymentData.bundleDispatchModalValidityHint({
                            days: "14",
                        })}
                    </p>
                </div>
            ),
            onOk: async () => {
                try {
                    const result = await sendBundleLink({
                        variables: {bookingId: booking.id, locale: "de"},
                    });
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const payload: any = (result?.data as any)?.sendMolliePaymentLinkForBookingFromAdmin;
                    const toastArgs = {
                        email: payload?.bookerEmail ?? recipientEmail,
                        amount: totalAmount.toFixed(2).replace(".", ","),
                        currency,
                    };
                    // #1110: Bei offener Mollie-Überweisung verschickt der Server die Zahlungsdaten erneut.
                    message.success(
                        payload?.dispatchKind === "banktransfer"
                            ? i18n.views.PanelCardPaymentData.bundleDispatchBanktransferSuccessToast(toastArgs)
                            : i18n.views.PanelCardPaymentData.bundleDispatchSuccessToast(toastArgs),
                        3,
                    );
                    refetchScheduledPayments();
                } catch (err) {
                    // Klassifikation via geteiltem Helper (extensions.code):
                    // MOLLIE_SCOPE_MISSING → Reconnect-Hinweis, sonst Rate-Limit/generisch.
                    const kind = classifyMollieDispatchError(err);
                    if (kind === "scopeMissing") {
                        // paymentlinks-7days: OAuth-Grant ohne Scope `payment-links.write`.
                        // Handlungsweisender Reconnect-Hinweis statt generischem Toast.
                        notification.warning({
                            message: i18n.containers.common.MollieReconnect.dispatchScopeMissingTitle(),
                            description: i18n.containers.common.MollieReconnect.dispatchScopeMissingDescription(),
                            duration: 0,
                        });
                    } else if (kind === "rateLimit") {
                        setBundleRateLimitUntil(Date.now() + 60 * 1000);
                        message.error(
                            i18n.containers.payments.OpenPaymentsList.dispatchRateLimitToast({
                                retryInMinutes: "60",
                            }),
                            5,
                        );
                    } else {
                        message.error(
                            i18n.containers.payments.OpenPaymentsList.dispatchGenericErrorToast(),
                            5,
                        );
                    }
                }
            },
        });
    };

    const isBundleRateLimited =
        bundleRateLimitUntil !== null && Date.now() < bundleRateLimitUntil;
    const isBundleDisabled = !canDispatchBundle || isBundleRateLimited || bundleLoading;

    type ContactCardCSSProperties = React.CSSProperties & {
        "--column-count": number;
    };

    const actualStyle: ContactCardCSSProperties = {
        "--column-count": 1,
        // ...style,
    };

    // Phase 23 / Plan 23-04 / BDSEC-06 — Mollie-Mode-Card-Title (mit Logo-Badge).
    // Tokens / HTML aus `.claude/skills/sketch-findings-ko-X-main/references/mollie-design-tokens.md`.
    const cardTitle = mollieMode ? (
        <Space size={8} align="center" data-testid="panel-card-payment-data-mollie-title">
            <span>{i18n.containers.invoice.InvoiceDetails.titlePayment()}</span>
            <span
                className={css.mollieLogoBadge}
                aria-label="Mollie"
                data-testid="panel-card-payment-data-mollie-logo"
            >
                {"mollie"}
            </span>
        </Space>
    ) : (
        i18n.containers.invoice.InvoiceDetails.titlePayment()
    );

    // BDSEC-06 — Edit-Action-Button + Bundle-Button werden im Mollie-Mode
    // ausgeblendet (Hosted-Checkout: Admin kann hier nichts editieren).
    // Nach DSGVO-Löschantrag lehnt der Server Zahlungsart-Wechsel, Zahlungslink und Erstattung ab.
    const attendeeErased = Boolean(booking.attendee?.erasedAt);
    const cardExtra = mollieMode
        ? null
        : !isCanceled &&
          !attendeeErased && (
                <Space size={4}>
                    {featureMollie && (
                        <Tooltip
                            title={
                                !canDispatchBundle
                                    ? i18n.views.PanelCardPaymentData.bundleDispatchButtonDisabledTooltip()
                                    : undefined
                            }
                        >
                            <Button
                                size="small"
                                icon={<LinkOutlined />}
                                disabled={isBundleDisabled}
                                loading={bundleLoading}
                                onClick={handleBundleDispatch}
                                data-testid={`bundle-dispatch-button-${booking.id}`}
                            >
                                {i18n.views.PanelCardPaymentData.bundleDispatchButton()}
                            </Button>
                        </Tooltip>
                    )}
                    {/* Phase 14 / Plan 14-05 (D-13, D-15): Standalone-Refund-Button.
                        Sichtbar bei featureMollie + mind. einer refund-faehigen Mollie-SP.
                        Modal zeigt alle refund-faehigen SPs im RefundPanel mode=single. */}
                    {featureMollie && refundableMollieSPs.length > 0 && (
                        <MollieRefundButton
                            scheduledPayment={refundableMollieSPs[0]}
                            additionalScheduledPayments={refundableMollieSPs.slice(1)}
                            onRefunded={refetchScheduledPayments}
                            size="small"
                        />
                    )}
                    <Button
                        size="small"
                        type="primary"
                        onClick={() => setEditPaymentModalOpen(true)}
                        data-testid={`edit-payment-method-button-${booking.id}`}
                    >
                        {"Bezahlmethode bearbeiten"}
                    </Button>
                </Space>
            );

    // BDSEC-06 — Mollie-Mode CSS-Klasse auf die Panel-Card-Wrapper-Div.
    const cardClassName = classNames(className, mollieMode && css.mollieMode);

    return (
        <PanelCard
            title={cardTitle}
            columns={paymentMethodType === "sepaDebit" ? 4 : 3}
            gapAfter
            extra={cardExtra}
            className={cardClassName}
            autoHeight={true}
        >
            {editPaymentModalOpen && (
                <EditPaymentModal booking={booking} refetchScheduledPayments={refetchScheduledPayments} onClose={() => setEditPaymentModalOpen(false)} />
            )}
            <div className={classNames(css.withDivider, css.container)} style={actualStyle}>
                <div className={css.label}>{i18n.containers.invoice.InvoiceDetails.paymentMethod()}</div>
                <div className={css.entry}>
                    <Space size={4}>
                        {/* OOS-3 (UAT 2026-05-17): Bei Mollie-Mode-Bookings, die noch
                            nicht final bezahlt sind (PaymentMethod noch unklar), zeigen
                            wir einen expliziten "Online-Zahlung"-Tag mit Tooltip-Erklärung.
                            Vorher wurde ein "unknown"/"Keine"-Tag gerendert, der Admins
                            verwirrte. Mehrere Trigger-Bedingungen:
                              (a) !isMolliePaid + paymentMethodType in {"none", "onlinePayment"}
                                  — Bucher hat noch nicht im Mollie-Checkout gewählt
                              (b) isMolliePaid + mollieMethod === "unknown" — Mollie-Attempt
                                  existiert (z.B. expired ohne Methode-Wahl), Backend liefert
                                  "unknown" als Default-mollieMethod */}
                        {mollieMode && (
                            (!isMolliePaid && (paymentMethodType === "none" || paymentMethodType === "onlinePayment"))
                            || (isMolliePaid && mollieSp?.mollieMethod === "unknown")
                        ) ? (
                            <Tooltip title="Der Bucher hat die konkrete Bezahlmethode (Kreditkarte, PayPal, Überweisung, ...) noch nicht im Mollie-Checkout gewählt. Nach erfolgreicher Bezahlung erscheint hier die tatsächlich genutzte Methode.">
                                <Tag style={{borderColor: "lightgrey"}}>{"Online-Zahlung"}</Tag>
                            </Tooltip>
                        ) : (
                            <PaymentMethodBadge
                                type={paymentMethodType}
                                paymentProvider={isMolliePaid ? "Mollie" : undefined}
                                mollieMethod={isMolliePaid && mollieSp ? mollieSp.mollieMethod : undefined}
                            />
                        )}
                        {!isMolliePaid && isMollieBankTransfer && paymentMethodType === "bankTransfer" && (
                            <Tag color="blue" style={{fontSize: 11, lineHeight: "18px", padding: "0 5px", margin: 0}}>
                                {"via Mollie"}
                            </Tag>
                        )}
                    </Space>
                </div>
            </div>
            {paymentMethodType === "sepaDebit" ? (
                <div className={classNames(css.withDivider, css.container)} style={actualStyle}>
                    <div className={css.label}>{i18n.containers.invoice.InvoiceDetails.sepaAccountholder()}</div>
                    <div className={css.entry}>{booking.paymentMethod?.sepaAccountholder}</div>

                    <div className={css.label}>{i18n.containers.invoice.InvoiceDetails.iban()}</div>
                    <div className={css.entry}>{booking.paymentMethod?.iban}</div>
                </div>
            ) : null}
            <div className={classNames(css.withDivider, css.container)} style={actualStyle}>
                <div className={css.label}>{i18n.containers.invoice.InvoiceDetails.turnus()}</div>
                <div className={css.entry}>{renderPaymentType(booking.paymentType)}</div>
            </div>
            <PriceOverview booking={booking} />
        </PanelCard>
    );
};

export default memo(PanelCardPaymentData);
